import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiTruck, FiShield, FiRefreshCw } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import { Product, Brand } from '../types';
import { productApi, brandApi } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
`;

const BrandSelectorSection = styled.section`
  background: #f8f9fa;
  padding: 2rem;
  border-bottom: 2px solid #e0e0e0;
`;

const BrandSelectorContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BrandLabel = styled.label`
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1.1rem;
`;

const BrandSelect = styled.select`
  flex: 1;
  max-width: 400px;
  padding: 12px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1a1a1a;
  }

  &:focus {
    outline: none;
    border-color: #1a1a1a;
    box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.1);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  color: #1a1a1a;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.2);
  }
`;

const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  background: #f8f9fa;
`;

const FeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: #1a1a1a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const ProductsSection = styled.section`
  padding: 4rem 2rem;
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const ViewAllButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #1a1a1a;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  margin: 0 auto;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.1rem;
  color: #666;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e74c3c;
`;

const HomePage: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const brandsData = await brandApi.getAllBrands(true);
        setBrands(brandsData);
        // Auto-select first brand
        if (brandsData.length > 0) {
          setSelectedBrandId(brandsData[0].id);
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };

    fetchBrands();
  }, []);

  // Fetch products when brand changes
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);
        const result = await productApi.searchProducts({
          brandId: selectedBrandId || undefined,
          limit: 8,
          isActive: true
        });
        setFeaturedProducts(result.products);
      } catch (err) {
        setError('Ürünler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    if (selectedBrandId) {
      fetchFeaturedProducts();
    }
  }, [selectedBrandId]);

  return (
    <Container>
      <BrandSelectorSection>
        <BrandSelectorContainer>
          <BrandLabel htmlFor="brand-select">Marka Seçin:</BrandLabel>
          <BrandSelect
            id="brand-select"
            value={selectedBrandId}
            onChange={(e) => setSelectedBrandId(e.target.value)}
          >
            <option value="">Tüm Markalar</option>
            {brands.map(brand => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </BrandSelect>
        </BrandSelectorContainer>
      </BrandSelectorSection>

      <HeroSection>
        <HeroContent>
          <HeroTitle>Modern Erkek Giyim</HeroTitle>
          <HeroSubtitle>
            Şıklığı ve konforu bir araya getiren, kaliteli erkek giyim koleksiyonumuzu keşfedin. 
            Her tarza uygun, özenle seçilmiş ürünlerle stilinizi yansıtın.
          </HeroSubtitle>
          <CTAButton to="/products">
            Koleksiyonu İncele
            <FiArrowRight />
          </CTAButton>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <FeaturesContainer>
          <FeatureCard>
            <FeatureIcon>
              <FiTruck size={24} />
            </FeatureIcon>
            <FeatureTitle>Ücretsiz Kargo</FeatureTitle>
            <FeatureDescription>
              500 TL ve üzeri alışverişlerinizde ücretsiz kargo avantajı
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiShield size={24} />
            </FeatureIcon>
            <FeatureTitle>Güvenli Alışveriş</FeatureTitle>
            <FeatureDescription>
              SSL sertifikası ile korunan güvenli ödeme sistemi
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiRefreshCw size={24} />
            </FeatureIcon>
            <FeatureTitle>Kolay İade</FeatureTitle>
            <FeatureDescription>
              14 gün içinde koşulsuz iade ve değişim imkanı
            </FeatureDescription>
          </FeatureCard>
        </FeaturesContainer>
      </FeaturesSection>

      <ProductsSection>
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Öne Çıkan Ürünler</SectionTitle>
            <SectionSubtitle>
              En popüler ve trend ürünlerimizi keşfedin
            </SectionSubtitle>
          </SectionHeader>

          {loading && (
            <LoadingContainer>
              Ürünler yükleniyor...
            </LoadingContainer>
          )}

          {error && (
            <ErrorContainer>
              {error}
            </ErrorContainer>
          )}

          {!loading && !error && (
            <>
              <ProductGrid>
                {featuredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </ProductGrid>

              <div style={{ textAlign: 'center' }}>
                <ViewAllButton to={`/products${selectedBrandId ? `?brandId=${selectedBrandId}` : ''}`}>
                  Tüm Ürünleri Gör
                  <FiArrowRight />
                </ViewAllButton>
              </div>
            </>
          )}
        </SectionContainer>
      </ProductsSection>
    </Container>
  );
};

export default HomePage;
